// @ts-check
// Pure filter predicate module — usable from theme.js (browser) and Vitest (Node).
// No DOM access here. Inputs come pre-normalized from normalizeProduct().
//
// Single source of truth for:
//   - SIZE_ALIASES + LETTER_TO_NUMERIC (size matching)
//   - FABRIC_MAP (collection_line → fabric label)
//   - COLOR_FAMILY_OVERRIDES (print-name disambiguation)
//   - FACET_VALUE_LABELS (chip / drawer display labels)
//   - productMatchesCategory + cardSizesMatchFilter + getColorFamily +
//     mapLengthToFilterLabel + getFabric + chipLabel
//
// In the browser, this module attaches `window.APFilterPredicates`.
// In Node (tests), it exports via CommonJS.

(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.APFilterPredicates = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {

  var SIZE_ALIASES = {
    'SML': 'S', 'SM': 'S', 'XSM': 'XS',
    'MED': 'M', 'MD': 'M',
    'LGE': 'L', 'XLG': 'XL', 'XXL': '2XL',
    '6T': '6', '8T': '8', '10T': '10', '12T': '12', '14T': '14', '16T': '16'
  };

  // #140: letter → numeric size mapping for lab coats / waist sizes
  var LETTER_TO_NUMERIC = {
    'XXS': /^0$/,
    'XS':  /^(2|32|34)$/,
    'S':   /^(4|6|34|36|38)L?$/,
    'M':   /^(8|10|38|40|42)L?$/,
    'L':   /^(12|14|42|44|46)L?$/,
    'XL':  /^(16|18|46|48|50)L?$/,
    '2XL': /^(20|50|52|54)L?$/,
    '3XL': /^(54|56)L?$/
  };

  var FABRIC_MAP = {
    'proflex': '2-Way Stretch', 'forward': '4-Way Stretch', 'eon': 'Rayon Blend',
    'momentum': '4-Way Stretch', 'blossom': 'Polyester Blend', 'red panda': 'Polyester Blend',
    'matrix': '4-Way Stretch', 'focus': '4-Way Stretch', 'essentials': 'Polycotton',
    'scrub zone': 'Polycotton', 'crft': '4-Way Stretch', 'vibe': 'Rayon Blend',
    'v-tess': 'Rayon Blend', 'core': 'Polycotton', 'knit': 'Polyester Blend',
    'bestee': 'Rayon Blend', 'blossom signature': 'Polyester Blend',
    'core program': 'Polycotton', 'eon sport': 'Rayon Blend',
    'essential lab coats': 'Polycotton', 'fit': 'Rayon Blend',
    'matrix basic': '4-Way Stretch', 'matrix impulse': '4-Way Stretch',
    'matrix mens': '4-Way Stretch', 'matrix pro': '4-Way Stretch',
    'matrix pro mens': '4-Way Stretch', 'matrix underscrub': '4-Way Stretch',
    'mens': 'Polycotton', 'prints': 'Polyester Blend',
    'redpanda': 'Polyester Blend', 'scrub cap': 'Polyester Blend'
  };

  // #121: explicit overrides for compound print names where naive longest-match
  // would file the wrong family. Keys must be lowercased.
  var COLOR_FAMILY_OVERRIDES = {
    'sunshine blossoms':     'Yellow',
    'cherry berry lemonade': 'Yellow',
    'pink grapefruit':       'Pink'
  };

  var FACET_VALUE_LABELS = {
    price: { '0-10': 'Under $10', '10-20': '$10 – $20', '20-30': '$20 – $30', '30-999': '$30+' }
  };

  // Color families are built at init from AP_COLOR_MAP.masterGroups.
  // Tests inject a synthetic map via createPredicates({ colorFamilies }).
  function buildColorFamiliesFromMasterGroups(masterGroups) {
    var families = {};
    if (!masterGroups || !masterGroups.length) return families;
    for (var i = 0; i < masterGroups.length; i++) {
      var g = masterGroups[i];
      families[g.name] = g.aliases || [];
    }
    return families;
  }

  function matchAtWordBoundary(lower, term) {
    var searchFrom = 0;
    while (true) {
      var pos = lower.indexOf(term, searchFrom);
      if (pos === -1) return false;
      if (pos === 0 || !/[a-z]/.test(lower.charAt(pos - 1))) return true;
      searchFrom = pos + 1;
    }
  }

  function createPredicates(opts) {
    opts = opts || {};
    var colorFamilies = opts.colorFamilies ||
      (typeof globalThis !== 'undefined' && globalThis.AP_COLOR_MAP &&
        buildColorFamiliesFromMasterGroups(globalThis.AP_COLOR_MAP.masterGroups)) ||
      {};

    function getColorFamily(colorName) {
      if (!colorName) return '';
      var lower = String(colorName).toLowerCase().replace(/\s*\([^)]*\)\s*$/, '').trim();
      if (COLOR_FAMILY_OVERRIDES[lower]) return COLOR_FAMILY_OVERRIDES[lower];
      var bestFamily = '';
      var bestLen = 0;
      for (var family in colorFamilies) {
        var terms = colorFamilies[family] || [];
        for (var i = 0; i < terms.length; i++) {
          var t = terms[i];
          if (t.length > bestLen && matchAtWordBoundary(lower, t)) {
            bestFamily = family;
            bestLen = t.length;
          }
        }
      }
      return bestFamily || 'Print';
    }

    function getFabric(collectionLine) {
      if (!collectionLine) return '';
      var key = String(collectionLine).toLowerCase();
      return FABRIC_MAP[key] || '';
    }

    function mapLengthToFilterLabel(val) {
      var v = String(val || '').toLowerCase();
      // French storefront: Régulier / Grand / Court / Petit.
      if (v === 'petite' || v === 'short' || v === 'court' || v === 'petit') return 'Petite / Short';
      if (v === 'regular' || v === 'régulier' || v === 'regulier') return 'Regular';
      if (v === 'tall' || v === 'grand') return 'Tall';
      return val;
    }

    // Given a card's pre-normalized size list and a selected filter value,
    // does any size in the list satisfy the filter? Literal letter match first,
    // then letter → numeric range fallback (#140).
    function cardSizesMatchFilter(normalizedCardSizes, filterValue) {
      if (!normalizedCardSizes || !normalizedCardSizes.length) return false;
      var fv = String(filterValue).toUpperCase();
      if (normalizedCardSizes.indexOf(fv) !== -1) return true;
      var numericRe = LETTER_TO_NUMERIC[fv];
      if (numericRe) {
        for (var i = 0; i < normalizedCardSizes.length; i++) {
          if (numericRe.test(normalizedCardSizes[i])) return true;
        }
      }
      return false;
    }

    // Normalize raw card sizes (CSV string) into the alias-resolved array
    // the predicate operates on. Mirrors theme.js normalizeProduct(dom).
    function normalizeCardSizes(rawCsv) {
      return String(rawCsv || '')
        .toUpperCase()
        .split(',')
        .map(function (s) { return SIZE_ALIASES[s] || s; })
        .filter(Boolean);
    }

    // p is the normalizeProduct shape:
    //   { gender, type, brand, sizes[], lengths[], colorFamilies[], fabric, price }
    function productMatchesCategory(p, cat, vals) {
      if (!vals || vals.length === 0) return true;
      switch (cat) {
        case 'type':   return vals.indexOf(p.type) !== -1;
        case 'brand':  return vals.indexOf(p.brand) !== -1;
        case 'size':   return vals.some(function (s) { return cardSizesMatchFilter(p.sizes, s); });
        case 'length': return vals.some(function (l) { return (p.lengths || []).indexOf(l) !== -1; });
        case 'color':  return vals.some(function (c) { return (p.colorFamilies || []).indexOf(c) !== -1; });
        case 'fabric': return vals.indexOf(p.fabric) !== -1;
        case 'price':
          return vals.some(function (range) {
            var parts = String(range).split('-');
            var bMin = parseFloat(parts[0]);
            var bMax = parseFloat(parts[1]);
            return p.price >= bMin && p.price < bMax;
          });
        case 'gender':
          // Convenience — gender uses a single active value in theme.js, but
          // this allows mass-checking from tests.
          return vals.indexOf(p.gender) !== -1;
        default: return true;
      }
    }

    function chipLabel(facet, value) {
      return (FACET_VALUE_LABELS[facet] && FACET_VALUE_LABELS[facet][value]) || value;
    }

    return {
      SIZE_ALIASES: SIZE_ALIASES,
      LETTER_TO_NUMERIC: LETTER_TO_NUMERIC,
      FABRIC_MAP: FABRIC_MAP,
      COLOR_FAMILY_OVERRIDES: COLOR_FAMILY_OVERRIDES,
      FACET_VALUE_LABELS: FACET_VALUE_LABELS,
      colorFamilies: colorFamilies,
      getColorFamily: getColorFamily,
      getFabric: getFabric,
      mapLengthToFilterLabel: mapLengthToFilterLabel,
      cardSizesMatchFilter: cardSizesMatchFilter,
      normalizeCardSizes: normalizeCardSizes,
      productMatchesCategory: productMatchesCategory,
      chipLabel: chipLabel
    };
  }

  return {
    createPredicates: createPredicates,
    buildColorFamiliesFromMasterGroups: buildColorFamiliesFromMasterGroups
  };
});
